import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { 
  Building2, 
  Layers, 
  DoorOpen, 
  ChevronRight, 
  ChevronDown, 
  Plus,
  MoreHorizontal,
  Trash2,
  Wand2
} from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export function TreeBuilder() {
  const { state, actions } = useApp();
  const { currentQuote, selectedNode } = state;
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [addDialog, setAddDialog] = useState({ open: false, type: null, parentId: null, floorId: null });
  const [formData, setFormData] = useState({ name: '', count: 10, prefix: '', floorRange: '1-10' });

  const toggleExpand = (nodeId) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const handleSelect = (type, id, path) => {
    actions.selectNode({ type, id, path });
  };

  const isSelected = (type, id) => {
    return selectedNode?.type === type && selectedNode?.id === id;
  };

  const openAddDialog = (type, parentId = null, floorId = null) => {
    const defaultPrefix = type === 'doors' && currentQuote?.buildings?.find(b => b.id === parentId)?.floors?.find(f => f.id === floorId)?.name || 'D';
    setFormData({ 
      name: '', 
      count: 10, 
      prefix: defaultPrefix.replace(/\s+/g, '').substring(0, 3).toUpperCase(),
      floorRange: '1-10'
    });
    setAddDialog({ open: true, type, parentId, floorId });
  };

  const handleAdd = () => {
    const { type, parentId, floorId } = addDialog;
    
    if (type === 'building') {
      if (!formData.name.trim()) {
        toast.error('Please enter a building name');
        return;
      }
      actions.addBuilding(formData.name);
      toast.success(`Building "${formData.name}" added`);
    } else if (type === 'floor') {
      if (!formData.name.trim()) {
        toast.error('Please enter a floor name');
        return;
      }
      actions.addFloor(parentId, formData.name);
      toast.success(`Floor "${formData.name}" added`);
      setExpandedNodes(prev => new Set([...prev, parentId]));
    } else if (type === 'door') {
      if (!formData.name.trim()) {
        toast.error('Please enter a door name');
        return;
      }
      actions.addDoor(parentId, floorId, formData.name, 'standard');
      toast.success(`Door "${formData.name}" added`);
      setExpandedNodes(prev => new Set([...prev, parentId, floorId]));
    } else if (type === 'doors') {
      if (!formData.prefix.trim() || formData.count < 1) {
        toast.error('Please enter a valid prefix and count');
        return;
      }
      actions.addMultipleDoors(parentId, floorId, formData.prefix, formData.count, 'standard');
      toast.success(`${formData.count} doors added`);
      setExpandedNodes(prev => new Set([...prev, parentId, floorId]));
    } else if (type === 'floors') {
      const range = formData.floorRange.trim();
      if (!range) {
        toast.error('Please enter a floor range or names');
        return;
      }
      actions.addMultipleFloors(parentId, range);
      // Count how many will be added for the toast
      const rangeMatch = range.match(/^(\d+)\s*-\s*(\d+)$/);
      const floorCount = rangeMatch
        ? Math.max(0, parseInt(rangeMatch[2]) - parseInt(rangeMatch[1]) + 1)
        : range.split(',').filter(s => s.trim()).length;
      toast.success(`${floorCount} floor${floorCount !== 1 ? 's' : ''} added`);
      setExpandedNodes(prev => new Set([...prev, parentId]));
    }
    
    setAddDialog({ open: false, type: null, parentId: null, floorId: null });
  };

  const handleAutoAssign = (scope, buildingId, floorId = null) => {
    actions.autoAssignHardware(scope, buildingId, floorId);
    toast.success('Hardware auto-assigned based on door types');
  };

  if (!currentQuote) return null;

  return (
    <div className="h-full flex flex-col border-r border-border bg-white" data-testid="tree-builder">
      {/* Tree Header */}
      <div className="p-3 border-b border-border flex items-center justify-between shrink-0">
        <h3 className="font-medium text-sm">Door Schedule</h3>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => openAddDialog('building')}
          data-testid="add-building-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Building
        </Button>
      </div>

      {/* Tree Content */}
      <ScrollArea className="flex-1">
        <div className="p-2">
          {currentQuote.buildings.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              <Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No buildings yet</p>
              <p className="text-xs">Add a building to get started</p>
            </div>
          ) : (
            currentQuote.buildings.map((building) => (
              <BuildingNode
                key={building.id}
                building={building}
                expanded={expandedNodes.has(building.id)}
                onToggle={() => toggleExpand(building.id)}
                onSelect={handleSelect}
                isSelected={isSelected}
                expandedNodes={expandedNodes}
                onToggleFloor={(floorId) => toggleExpand(floorId)}
                onAddFloor={() => openAddDialog('floor', building.id)}
                onAddFloors={() => openAddDialog('floors', building.id)}
                onAddDoor={(floorId) => openAddDialog('door', building.id, floorId)}
                onAddDoors={(floorId) => openAddDialog('doors', building.id, floorId)}
                onDelete={actions.deleteBuilding}
                onDeleteFloor={actions.deleteFloor}
                onDeleteDoor={actions.deleteDoor}
                onAutoAssign={handleAutoAssign}
              />
            ))
          )}
        </div>
      </ScrollArea>

      {/* Add Dialog */}
      <Dialog open={addDialog.open} onOpenChange={(open) => setAddDialog({ ...addDialog, open })}>
        <DialogContent data-testid="add-dialog">
          <DialogHeader>
            <DialogTitle>
              {addDialog.type === 'building' && 'Add Building'}
              {addDialog.type === 'floor' && 'Add Floor'}
              {addDialog.type === 'floors' && 'Add Multiple Floors'}
              {addDialog.type === 'door' && 'Add Door'}
              {addDialog.type === 'doors' && 'Add Multiple Doors'}
            </DialogTitle>
            <DialogDescription>
              {addDialog.type === 'doors' && 'Add multiple doors at once with a naming prefix'}
              {addDialog.type === 'floors' && 'Enter a range like 1-10 or 1-90, or comma-separated names like Ground, L1, L2'}
              {(addDialog.type === 'building' || addDialog.type === 'floor' || addDialog.type === 'door') && `Enter the name for the new ${addDialog.type}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {addDialog.type === 'floors' ? (
              <div className="space-y-2">
                <Label htmlFor="floorRange">Floor Range or Names</Label>
                <Input
                  id="floorRange"
                  value={formData.floorRange}
                  onChange={(e) => setFormData({ ...formData, floorRange: e.target.value })}
                  placeholder="e.g. 1-10, or Ground, L1, L2, L3"
                  data-testid="floor-range-input"
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  Range (e.g. <strong>1-90</strong>) adds 90 floors. Comma-separated names add exactly those floors.
                </p>
              </div>
            ) : addDialog.type === 'doors' ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="prefix">Name Prefix</Label>
                  <Input
                    id="prefix"
                    value={formData.prefix}
                    onChange={(e) => setFormData({ ...formData, prefix: e.target.value })}
                    placeholder="e.g., GF, L1, FIRE"
                    data-testid="door-prefix-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Doors will be named: {formData.prefix}-001, {formData.prefix}-002, etc.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="count">Number of Doors</Label>
                  <Input
                    id="count"
                    type="number"
                    min="1"
                    max="500"
                    value={formData.count}
                    onChange={(e) => setFormData({ ...formData, count: parseInt(e.target.value) || 1 })}
                    data-testid="door-count-input"
                  />
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={`Enter ${addDialog.type} name`}
                  data-testid="name-input"
                  autoFocus
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog({ ...addDialog, open: false })}>
              Cancel
            </Button>
            <Button onClick={handleAdd} data-testid="confirm-add-btn">
              {addDialog.type === 'doors' ? `Add ${formData.count} Doors` : addDialog.type === 'floors' ? 'Add Floors' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BuildingNode({ 
  building, 
  expanded, 
  onToggle, 
  onSelect, 
  isSelected,
  expandedNodes,
  onToggleFloor,
  onAddFloor,
  onAddFloors,
  onAddDoor,
  onAddDoors,
  onDelete,
  onDeleteFloor,
  onDeleteDoor,
  onAutoAssign
}) {
  const doorCount = building.floors.reduce((sum, f) => sum + f.doors.length, 0);

  return (
    <div className="mb-1" data-testid={`tree-node-building-${building.id}`}>
      {/* Building Row */}
      <div 
        className={cn(
          "flex items-center gap-1 px-2 py-1.5 rounded-md cursor-pointer group hover:bg-muted/50 transition-colors",
          isSelected('building', building.id) && "bg-accent"
        )}
      >
        <button 
          onClick={onToggle}
          className="p-0.5 hover:bg-muted rounded"
          data-testid={`toggle-building-${building.id}`}
        >
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
        </button>
        <Building2 className="w-4 h-4 text-primary shrink-0" />
        <span 
          className="flex-1 text-sm font-medium truncate"
          onClick={() => onSelect('building', building.id, { buildingId: building.id })}
        >
          {building.name}
        </span>
        <span className="text-xs text-muted-foreground mr-1">
          {building.floors.length}F / {doorCount}D
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button 
              className="p-1 opacity-0 group-hover:opacity-100 hover:bg-muted rounded transition-opacity"
              data-testid={`building-menu-${building.id}`}
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onAddFloor}>
              <Plus className="w-4 h-4 mr-2" />
              Add Floor
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onAddFloors}>
              <Plus className="w-4 h-4 mr-2" />
              Add Multiple Floors
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAutoAssign('building', building.id)}>
              <Wand2 className="w-4 h-4 mr-2" />
              Auto Assign Hardware
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="text-destructive"
              onClick={() => onDelete(building.id)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Building
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Floors */}
      {expanded && (
        <div className="ml-4 pl-2 border-l border-border">
          {building.floors.length === 0 ? (
            <div className="py-2 px-2 text-xs text-muted-foreground">
              No floors yet
            </div>
          ) : (
            building.floors.map((floor) => (
              <FloorNode
                key={floor.id}
                floor={floor}
                buildingId={building.id}
                expanded={expandedNodes.has(floor.id)}
                onToggle={() => onToggleFloor(floor.id)}
                onSelect={onSelect}
                isSelected={isSelected}
                onAddDoor={() => onAddDoor(floor.id)}
                onAddDoors={() => onAddDoors(floor.id)}
                onDelete={() => onDeleteFloor(building.id, floor.id)}
                onDeleteDoor={(doorId) => onDeleteDoor(building.id, floor.id, doorId)}
                onAutoAssign={() => onAutoAssign('floor', building.id, floor.id)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function FloorNode({ 
  floor, 
  buildingId, 
  expanded, 
  onToggle, 
  onSelect, 
  isSelected,
  onAddDoor,
  onAddDoors,
  onDelete,
  onDeleteDoor,
  onAutoAssign
}) {
  return (
    <div className="mb-0.5" data-testid={`tree-node-floor-${floor.id}`}>
      {/* Floor Row */}
      <div 
        className={cn(
          "flex items-center gap-1 px-2 py-1.5 rounded-md cursor-pointer group hover:bg-muted/50 transition-colors",
          isSelected('floor', floor.id) && "bg-accent"
        )}
      >
        <button 
          onClick={onToggle}
          className="p-0.5 hover:bg-muted rounded"
          data-testid={`toggle-floor-${floor.id}`}
        >
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
        </button>
        <Layers className="w-4 h-4 text-amber-600 shrink-0" />
        <span 
          className="flex-1 text-sm truncate"
          onClick={() => onSelect('floor', floor.id, { buildingId, floorId: floor.id })}
        >
          {floor.name}
        </span>
        <span className="text-xs text-muted-foreground mr-1">
          {floor.doors.length}D
        </span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button 
              className="p-1 opacity-0 group-hover:opacity-100 hover:bg-muted rounded transition-opacity"
              data-testid={`floor-menu-${floor.id}`}
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onAddDoor}>
              <Plus className="w-4 h-4 mr-2" />
              Add Door
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onAddDoors}>
              <Plus className="w-4 h-4 mr-2" />
              Add Multiple Doors
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onAutoAssign}>
              <Wand2 className="w-4 h-4 mr-2" />
              Auto Assign Hardware
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Floor
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Doors */}
      {expanded && (
        <div className="ml-4 pl-2 border-l border-border">
          {floor.doors.length === 0 ? (
            <div className="py-2 px-2 text-xs text-muted-foreground">
              No doors yet
            </div>
          ) : (
            floor.doors.map((door) => (
              <DoorNode
                key={door.id}
                door={door}
                buildingId={buildingId}
                floorId={floor.id}
                onSelect={onSelect}
                isSelected={isSelected}
                onDelete={() => onDeleteDoor(door.id)}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function DoorNode({ door, buildingId, floorId, onSelect, isSelected, onDelete }) {
  const doorTypeColors = {
    'standard': 'text-slate-500',
    'exit': 'text-green-600',
    'fire-rated': 'text-red-500'
  };

  return (
    <div 
      className={cn(
        "flex items-center gap-1 px-2 py-1 rounded-md cursor-pointer group hover:bg-muted/50 transition-colors",
        isSelected('door', door.id) && "bg-accent"
      )}
      onClick={() => onSelect('door', door.id, { buildingId, floorId, doorId: door.id })}
      data-testid={`tree-node-door-${door.id}`}
    >
      <div className="w-5" /> {/* Spacer for alignment */}
      <DoorOpen className={cn("w-4 h-4 shrink-0", doorTypeColors[door.type])} />
      <span className="flex-1 text-sm truncate">{door.name}</span>
      <span className="text-xs text-muted-foreground capitalize">
        {door.type === 'fire-rated' ? 'Fire' : door.type}
      </span>
      {door.products.length > 0 && (
        <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
          {door.products.length}
        </span>
      )}
      <button 
        className="p-1 opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive rounded transition-all"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        data-testid={`delete-door-${door.id}`}
      >
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}
